// UI strings for listening ("Kuuntele"): the listen-and-translate exercise, the clip player, the
// scene list and offline audio. Finnish (default) and English side by side; en has the same shape.
// Style: docs/FINNISH-STYLE.md (no internal jargon, sinä-muoto, "1 repliikki", "2 repliikkiä").
import { defineArea } from '../area';

const fiNum = (n: number, digits = 0) => n.toLocaleString('fi-FI', { minimumFractionDigits: digits, maximumFractionDigits: digits });
const enNum = (n: number, digits = 0) => n.toLocaleString('en-GB', { minimumFractionDigits: digits, maximumFractionDigits: digits });

/** Bytes as "12,3 Mt" / "12.3 MB". */
function bytesFi(b: number): string {
  if (b >= 1e9) return `${fiNum(b / 1e9, 1)} Gt`;
  if (b >= 1e6) return `${fiNum(b / 1e6, 1)} Mt`;
  return `${fiNum(Math.max(1, Math.round(b / 1e3)))} kt`;
}
function bytesEn(b: number): string {
  if (b >= 1e9) return `${enNum(b / 1e9, 1)} GB`;
  if (b >= 1e6) return `${enNum(b / 1e6, 1)} MB`;
  return `${enNum(Math.max(1, Math.round(b / 1e3)))} kB`;
}

export const listenFi = {
  title: 'Kuuntele',
  door: 'Kuuntele sarjojen repliikkejä',
  kinds: {
    translate: 'Kuuntele ja käännä',
    dictation: 'Kirjoita kuulemasi valyriaksi',
    word: 'Mitä sanaa kuulit?',
  } as Record<'translate' | 'dictation' | 'word', string>,
  translateLabel: 'Mitä repliikki tarkoittaa? Kirjoita suomeksi tai englanniksi.',
  dictationLabel: 'Kirjoita valyriaksi, mitä kuulit',
  wordQ: 'Valitse sana, jonka kuulit.',
  keys: 'Välilyönti toistaa, Enter tarkistaa.',
  player: {
    play: 'Toista',
    pause: 'Tauko',
    replay: 'Alusta',
    slow: 'Hidas 0,75×',
    loop: 'Toista jatkuvasti',
    take: (n: number, of: number) => `äänite ${n}/${of}`,
    label: 'Äänite',
    failed: 'Äänitettä ei voitu toistaa. Jos olet ilman yhteyttä, lataa äänet laitteelle ensin.',
    plays: (n: number) => (n === 1 ? 'kuunneltu kerran' : `kuunneltu ${n} kertaa`),
  },
  reveal: {
    line: 'Repliikki',
    official: 'Virallinen käännös',
    finnish: 'Suomeksi',
    caught: 'Tunnistit',
    missed: 'Jäi kuulematta',
    wordsH: 'Sanat',
  },
  practice: {
    h: 'Harjoittele',
    pool: { open: 'Sinulle avatut', all: 'Kaikki repliikit' },
    poolLabel: 'Mistä repliikit otetaan',
    modesLabel: 'Tehtävät',
    openNote: (n: number) =>
      n === 1
        ? '1 repliikki, jonka sanoista vähintään 80\u00a0% on jo tullut sinulle vastaan.'
        : `${n} repliikkiä, joiden sanoista vähintään 80\u00a0% on jo tullut sinulle vastaan.`,
    allNote: (n: number) => `Kaikki ${n} äänitettyä repliikkiä. Kuunteleminen ei avaa uusia sanoja korteiksi.`,
    openEmpty: 'Sinulle avatuissa sanoissa ei vielä ole äänitettyjä repliikkejä. Kokeile kaikkia repliikkejä tai palaa, kun olet oppinut lisää sanoja.',
    start: (n: number) => (n === 1 ? 'Aloita (1 tehtävä)' : `Aloita (${n} tehtävää)`),
    again: 'Uusi kierros',
    back: 'Takaisin listaan',
    loading: 'Valmistellaan sanakirjaa…',
    reading: 'Luetaan repliikkejä…',
  },
  scenes: {
    h: 'Kohtaukset',
    scene: (n: number) => `Kohtaus ${n}`,
    lines: (n: number) => (n === 1 ? '1 repliikki' : `${n} repliikkiä`),
    playScene: 'Toista kohtaus',
    stopScene: 'Pysäytä',
    practiceScene: 'Harjoittele kohtausta',
    showText: 'Näytä tekstit',
    hideText: 'Piilota tekstit',
    other: 'Muut äänitteet',
    season: (show: string, s: number) => `${show}, kausi ${s}`,
    playLine: (who: string) => `Toista: ${who}`,
    nowPlaying: 'Soi nyt',
  },
  offline: {
    h: 'Äänet laitteelle',
    button: (size: number) => `Lataa äänet laitteelle (${bytesFi(size)})`,
    stop: 'Keskeytä',
    progress: (n: number, of: number) => `Ladattu ${n}/${of}`,
    have: (n: number, of: number) => (n === of ? `Kaikki ${of} äänitettä ovat laitteella.` : `Laitteella ${n}/${of} äänitettä.`),
    failed: (n: number) => (n === 1 ? '1 äänitteen lataus epäonnistui. Yritä uudelleen.' : `${n} äänitteen lataus epäonnistui. Yritä uudelleen.`),
    storage: (used: number) => `Äänet ja sanakirja vievät laitteelta ${bytesFi(used)}.`,
    lowSpace: 'Laitteen tila on vähissä: kaikki äänet eivät ehkä mahdu.',
    notPersisted: 'Selain voi poistaa ladatut äänet, jos laitteen tila käy vähiin.',
    offlineNow: 'Ei yhteyttä. Lataa äänet, kun yhteys palaa.',
    unsupported: 'Tämä selain ei pysty tallentamaan ääniä laitteelle.',
  },
};

export const listenEn: typeof listenFi = {
  title: 'Listen',
  door: 'Listen to lines from the shows',
  kinds: {
    translate: 'Listen and translate',
    dictation: 'Write what you hear in Valyrian',
    word: 'Which word did you hear?',
  },
  translateLabel: 'What does the line mean? Write in Finnish or English.',
  dictationLabel: 'Write in Valyrian what you heard',
  wordQ: 'Pick the word you heard.',
  keys: 'Space plays, Enter checks.',
  player: {
    play: 'Play',
    pause: 'Pause',
    replay: 'From the start',
    slow: 'Slow 0.75×',
    loop: 'Repeat',
    take: (n: number, of: number) => `recording ${n}/${of}`,
    label: 'Recording',
    failed: 'The recording could not be played. If you are offline, download the audio to this device first.',
    plays: (n: number) => (n === 1 ? 'heard once' : `heard ${n} times`),
  },
  reveal: {
    line: 'The line',
    official: 'Official translation',
    finnish: 'In Finnish',
    caught: 'You caught',
    missed: 'Missed',
    wordsH: 'Words',
  },
  practice: {
    h: 'Practise',
    pool: { open: 'Open to you', all: 'All lines' },
    poolLabel: 'Which lines',
    modesLabel: 'Tasks',
    openNote: (n: number) =>
      n === 1
        ? '1 line in which at least 80% of the words have already come up for you.'
        : `${n} lines in which at least 80% of the words have already come up for you.`,
    allNote: (n: number) => `All ${n} recorded lines. Listening does not add new words as cards.`,
    openEmpty: 'None of the recorded lines are open to you yet. Try all lines, or come back once you know more words.',
    start: (n: number) => (n === 1 ? 'Start (1 task)' : `Start (${n} tasks)`),
    again: 'Another round',
    back: 'Back to the list',
    loading: 'Preparing the dictionary…',
    reading: 'Reading the lines…',
  },
  scenes: {
    h: 'Scenes',
    scene: (n: number) => `Scene ${n}`,
    lines: (n: number) => (n === 1 ? '1 line' : `${n} lines`),
    playScene: 'Play the scene',
    stopScene: 'Stop',
    practiceScene: 'Practise this scene',
    showText: 'Show the text',
    hideText: 'Hide the text',
    other: 'Other recordings',
    season: (show: string, s: number) => `${show}, season ${s}`,
    playLine: (who: string) => `Play: ${who}`,
    nowPlaying: 'Playing',
  },
  offline: {
    h: 'Audio on this device',
    button: (size: number) => `Download the audio (${bytesEn(size)})`,
    stop: 'Stop',
    progress: (n: number, of: number) => `Downloaded ${n}/${of}`,
    have: (n: number, of: number) => (n === of ? `All ${of} recordings are on this device.` : `${n}/${of} recordings on this device.`),
    failed: (n: number) => (n === 1 ? '1 recording failed to download. Try again.' : `${n} recordings failed to download. Try again.`),
    storage: (used: number) => `Audio and the dictionary take ${bytesEn(used)} on this device.`,
    lowSpace: 'This device is running low on space: not all audio may fit.',
    notPersisted: 'The browser may remove downloaded audio if the device runs low on space.',
    offlineNow: 'Offline. Download the audio when you are back online.',
    unsupported: 'This browser cannot store audio on the device.',
  },
};

// Messages.listen (i18n/area.ts: collected by area-registry.ts)
export const area = defineArea('listen', listenFi, listenEn);
declare module '../area' {
  interface AreaMessages extends Record<typeof area.key, typeof listenFi> {}
}
